import { clsx } from "clsx";
import pluralize from "pluralize";
import { Component, type ReactElement } from "react";
import { FocusScope } from "react-aria";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";

import { SymbolIcon } from "widgets";
import { isUndefinedOrEmptyArray } from "widgets/utils";

import styleMenu from "../styles/ContextualMenu.module.scss";
import styleColumnMenu from "./styles/AdvancedSearch.module.scss";

import { AdvancedSearch } from "../navbar/AdvancedSearch.js";
import { TooltipController } from "../overlays/components/OverlayControllers.js";
import AdvancedSearchBoolean from "./AdvancedSearchBoolean.js";
import AdvancedSearchDate from "./AdvancedSearchDate.js";
import AdvancedSearchNumber from "./AdvancedSearchNumber.js";
import AdvancedSearchOperator from "./AdvancedSearchOperator.js";
import AdvancedSearchSelect from "./AdvancedSearchSelect.js";
import AdvancedSearchString from "./AdvancedSearchString.js";
import AdvancedSearchStringListSelect from "./AdvancedSearchStringListSelect.js";
import AdvancedSearchVariantProfile from "./AdvancedSearchVariantProfile.js";
import { fieldIsInvalid, simpleDateValidation } from "@/app-utils/form.ts";
import { type IRsqlComparisonExpr, SEARCH_OPERATORS } from "@/app-utils/rsql.ts";
import { type InputTableQuerySpec, type SearchAttribute } from "@/graphql.ts";
import {
  CELL_TYPE,
  FEATURE_DISPLAY_NAME,
  MAX_SCIENTIFIC_NUMBER_EXPONENT,
  MIN_SCIENTIFIC_NUMBER_EXPONENT,
  VARIANT_TABLE_COLUMNS,
} from "@/settings/settings.ts";
import { FormError } from "@/ui-modules/core/form/layout/FormError.tsx";

export interface IAdvancedSearchCriteriaProps extends Partial<WrappedFieldProps> {
  searchAttribute?: SearchAttribute;
  tableQuerySpec?: InputTableQuerySpec; // to distinguish between variants and VCF variants

  noLabel?: boolean;
  noErrorMessage?: boolean;
  autoFocus?: boolean;
  verticalLayout?: boolean;
  handleRemoveCriteria?: () => void;
  immediatelyPreviewResults?: () => void;
  onOperatorChange?: (operator: string) => void; // useful in column menu to let the overlay component refresh
  children?: any;
  condensedView?: boolean; // show inputs one above the other, not inline
}

interface IAdvancedSearchCriteriaState {
  filterOperation?: IRsqlComparisonExpr;
}

// the following interface is common to all field types
export interface IAdvancedSearchFieldProps {
  filterOperation: IRsqlComparisonExpr;
  field: IAdvancedSearchCriteriaProps;
  autoFocus?: boolean;
  verticalLayout?: boolean;
  tableQuerySpec?: InputTableQuerySpec; // distinguish types into the profile column
  onChangeState: (partialState: Partial<IRsqlComparisonExpr>, immediatePreview?: boolean) => void;
  onBlur?: () => void;
}

/**
 * <description>
 *
 * created on 6/13/2017
 */
class AdvancedSearchCriteria extends Component<IAdvancedSearchCriteriaProps, IAdvancedSearchCriteriaState> {
  constructor(props: IAdvancedSearchCriteriaProps) {
    super(props);

    this.changeState = this.changeState.bind(this);
    this.updateFormState = this.updateFormState.bind(this);
    this.handleBlur = this.handleBlur.bind(this);

    // initialization of local state
    this.state = {
      filterOperation: {
        ...props.input.value,
        key: props.searchAttribute.field,
      },
    };
  }

  componentDidUpdate(
    prevProps: Readonly<IAdvancedSearchCriteriaProps>,
    _prevState: Readonly<IAdvancedSearchCriteriaState>,
    _snapshot?: any,
  ): void {
    // in case initialization of the value happened after this component has been mounted
    if (this.props.input.value !== prevProps.input.value) {
      this.setState({ filterOperation: { ...this.state.filterOperation, ...this.props.input.value } });
    }
  }

  updateFormState(immediatePreview: boolean) {
    this.props.input.onChange(this.state.filterOperation);

    if (this.props.immediatelyPreviewResults && immediatePreview) {
      setTimeout(this.props.immediatelyPreviewResults, 200); // timeout otherwise the preview happens before the redux-form state is updated !
    }
  }

  changeState(partialState: Partial<IRsqlComparisonExpr>, immediatePreview = true) {
    // after we changed the local state we update the form state (for updating redux-form)
    let stateChangedCallback: () => void = () => this.updateFormState(immediatePreview);
    // ...but it should not apply when changing the operator and there is no value yet, so that we don't trigger the validation while the user is not over
    if (
      partialState.operator &&
      (!this.state.filterOperation.value || (this.state.filterOperation.value as string).length === 0)
    ) {
      stateChangedCallback = () => {
        /* empty */
      };
    }
    if (partialState.operator && this.props.onOperatorChange) {
      this.props.onOperatorChange(partialState.operator);
    }
    this.setState({ filterOperation: { ...this.state.filterOperation, ...partialState } }, stateChangedCallback);
  }

  hasControlledVocabulary() {
    return this.props.searchAttribute.values && this.props.searchAttribute.values.length > 0;
  }

  isUnassignedUsergroupFilter = (): boolean => {
    return !!(
      this.state.filterOperation.key === "userGroups.name" && this.state.filterOperation.value === "Unassigned"
    );
  };

  renderInputComponent() {
    // simple workaround for the special case of an unassigned user group
    if (this.isUnassignedUsergroupFilter()) {
      return (
        <div>
          <strong>
            Showing unassigned{" "}
            {this.props.tableQuerySpec?.resultType
              ? pluralize(FEATURE_DISPLAY_NAME[this.props.tableQuerySpec.resultType])
              : "data"}
          </strong>
        </div>
      );
    }

    const advancedSearchFieldProps: IAdvancedSearchFieldProps = {
      filterOperation: this.state.filterOperation,
      field: this.props,
      onChangeState: this.changeState,
      onBlur: this.handleBlur,
      //autoFocus: this.props.autoFocus, // [SEL8-760] replaced by FocusScope component in the render, because the autoFocus seems to cause issues in context of modal dialogs (related to the FocusScope of the modal dialog)
      verticalLayout: this.props.verticalLayout,
    };

    if (this.hasControlledVocabulary()) {
      if (this.props.searchAttribute.cellType === CELL_TYPE.VARIANT_PROFILE) {
        return (
          <AdvancedSearchVariantProfile {...advancedSearchFieldProps} tableQuerySpec={this.props.tableQuerySpec} />
        );
      } else if (this.props.searchAttribute.field === VARIANT_TABLE_COLUMNS.VARIANT_GENOMES) {
        return <AdvancedSearchStringListSelect {...advancedSearchFieldProps} />;
      } else {
        return (
          <AdvancedSearchSelect
            {...advancedSearchFieldProps}
            menuPosition={this.props.condensedView ? "absolute" : "fixed"}
          />
        );
      }
    } else {
      switch (this.props.searchAttribute.cellType) {
        case CELL_TYPE.NUMBER_LIST:
        case CELL_TYPE.NUMBER:
        case CELL_TYPE.INTEGER:
        case CELL_TYPE.SCIENTIFIC:
          return <AdvancedSearchNumber {...advancedSearchFieldProps} />;
        case CELL_TYPE.BOOLEAN:
          return <AdvancedSearchBoolean {...advancedSearchFieldProps} />;
        case CELL_TYPE.DATE:
          return <AdvancedSearchDate {...advancedSearchFieldProps} withTime={false} />;
        case CELL_TYPE.DATE_TIME:
          return <AdvancedSearchDate {...advancedSearchFieldProps} withTime={true} />;
        case CELL_TYPE.VARIANT_PROFILE:
          return <AdvancedSearchVariantProfile {...advancedSearchFieldProps} />;
        default:
          return <AdvancedSearchString {...advancedSearchFieldProps} />;
      }
    }
  }

  handleBlur() {
    // The following causes issue (SEL6-2681, point 5)
    this.props.input.onBlur(this.state.filterOperation);
  }

  render(): ReactElement {
    const field: IAdvancedSearchCriteriaProps = this.props;
    const searchAttribute: SearchAttribute = field.searchAttribute;
    const { cellType } = this.props.searchAttribute;
    const useAdvSearch: boolean =
      cellType === CELL_TYPE.BOOLEAN ||
      cellType === CELL_TYPE.VARIANT_PROFILE ||
      field.searchAttribute.field === VARIANT_TABLE_COLUMNS.VARIANT_GENOMES;
    const isUnassignedUsergroupFilter: boolean = this.isUnassignedUsergroupFilter();
    const focusOnInput = !(
      // disable auto-focus due to SEL8-751
      (
        this.props.searchAttribute.cellType === CELL_TYPE.DATE ||
        this.props.searchAttribute.cellType === CELL_TYPE.DATE_TIME
      )
    );

    return (
      <div
        className={clsx(styleColumnMenu.container, {
          [styleColumnMenu.advanced_search_condensed]: this.props.condensedView,
        })}
      >
        {!field.noLabel && (
          <span
            className={this.props.condensedView ? styleMenu.category_title : styleColumnMenu.advanced_search_label}
            style={{ marginLeft: this.props.condensedView ? 0 : undefined }}
          >
            {field.verticalLayout && "Filter by "}
            {AdvancedSearch.getAttributeOptionLabel(searchAttribute)}
            {searchAttribute.description ? ` (${searchAttribute.description})` : ""}
          </span>
        )}
        <div className={styleColumnMenu.advanced_search_form}>
          {!useAdvSearch && !isUnassignedUsergroupFilter && (
            <AdvancedSearchOperator
              filterOperation={this.state.filterOperation}
              field={this.props}
              onChangeState={this.changeState}
              menuPosition={this.props.condensedView ? "absolute" : "fixed"}
            />
          )}
          <div className={styleColumnMenu.advanced_search_value_input}>
            <div className={styleColumnMenu.advanced_search_value_field}>
              <FocusScope autoFocus={focusOnInput}>{this.renderInputComponent()}</FocusScope>
            </div>
            {field.handleRemoveCriteria && (
              <TooltipController content={"Remove this search criteria from the form"}>
                {(tooltipTriggerProps) => (
                  <div
                    className={styleColumnMenu.advanced_search_value_remove}
                    onClick={field.handleRemoveCriteria}
                    role={"button"}
                    onPointerEnter={tooltipTriggerProps.onPointerEnter}
                    onPointerLeave={tooltipTriggerProps.onPointerLeave}
                    ref={tooltipTriggerProps.ref}
                  >
                    <SymbolIcon icon={"close"} />
                  </div>
                )}
              </TooltipController>
            )}
          </div>
        </div>
        {this.props.children}
        {!field.noErrorMessage && fieldIsInvalid(field) && <FormError>{field.meta.error}</FormError>}
      </div>
    );
  }
}

function scientificExponentIsValid(value: string) {
  const valueString: string = value?.toLowerCase();
  const exponent = Number(valueString?.indexOf("e") >= 0 ? valueString.substring(valueString.indexOf("e") + 1) : "");
  return exponent >= MIN_SCIENTIFIC_NUMBER_EXPONENT && exponent <= MAX_SCIENTIFIC_NUMBER_EXPONENT;
}

export function standardAdvancedSearchCriteriaValidation(filterOperation: IRsqlComparisonExpr): string {
  if (!filterOperation?.value || (filterOperation.value as string).length === 0) {
    return `A search value is required.`;
  }

  if (filterOperation.operator === SEARCH_OPERATORS.BETWEEN) {
    if (!filterOperation.value2 || filterOperation.value2.length === 0) {
      return `The min/max range is required`;
    }
  }
}

export function validateFilterOperation(filterOperation: IRsqlComparisonExpr, cellType: string) {
  if (cellType === CELL_TYPE.VARIANT_PROFILE) {
    // at least one field must be populated
    const isMustBePresentEmpty = !filterOperation || isUndefinedOrEmptyArray(filterOperation.value);
    const isMustBeAbsentEmpty = !filterOperation || isUndefinedOrEmptyArray(filterOperation.value2);
    if (isMustBePresentEmpty && isMustBeAbsentEmpty) {
      return `At least one absence/presence value is required.`;
    }
  }

  if (filterOperation && (cellType === CELL_TYPE.DATE || cellType === CELL_TYPE.DATE_TIME)) {
    if (filterOperation.value) {
      const error = simpleDateValidation(filterOperation.value);
      if (error) {
        return error;
      }
    }
    if (filterOperation.value2) {
      const error = simpleDateValidation(filterOperation.value2);
      if (error) {
        return error;
      }
    }
  }

  if (cellType === CELL_TYPE.SCIENTIFIC) {
    // Disable the filter button if the exponent is out of range.
    // converting the string to a number (using Number() or parseFloat()) does not work here
    // because e.g. 1e-999 is converted to 0)
    if (
      filterOperation &&
      (!scientificExponentIsValid(filterOperation.value as string) ||
        !scientificExponentIsValid(filterOperation.value2 as string))
    ) {
      return `The exponent of the values is out of range`;
    }
  }

  // the rest at the end, since some standard validation might not apply
  if (cellType !== CELL_TYPE.VARIANT_PROFILE) {
    // standard validation does not apply to variant profiles which has different constraints wrt value/value2
    return standardAdvancedSearchCriteriaValidation(filterOperation);
  }
}

export function AdvancedSearchCriteriaReduxFormField(props: IAdvancedSearchCriteriaProps & BaseFieldProps) {
  return <Field {...props} />;
}

export default AdvancedSearchCriteria;
